/**
 * F34 (Wave 2D) — admin trainer package-consumption reversal worklist.
 *
 * The single F34 admin surface. There is no GET-consumption-by-id endpoint at
 * F34 and no existing admin detail page that carries a `consumptionId` (the
 * admin booking detail carries a booking id; there is no read endpoint to map
 * a booking → its consumption). So this is a deliberate lookup-by-id + reverse
 * tool page — the support/super_admin actor pastes the consumption id (surfaced
 * to them via CloudWatch `trainer.event=package_consumption_*` logs or a
 * client's report) and reverses the decrement with a mandatory, audit-logged
 * reason.
 *
 * Route-gated on `trainer_package_consumption:reverse` (super_admin + support)
 * via <ProtectedRoute> in App.tsx; the page additionally hides the action for
 * any actor missing the permission (defence-in-depth — the server re-enforces).
 */
import { useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, Card, FormField, TextInput, useToast } from '@bravemove/ui-web';
import type { ReverseTrainerConsumptionInput } from '@bravemove/types';
import { useAuth } from '../../auth/useAuth';
import { PageHeader } from '../../components/PageHeader';
import { ReverseConsumptionDialog } from '../../components/trainer/ReverseConsumptionDialog';
import { adminTrainerConsumptionsApi } from '../../api/trainerConsumptions';

// Loose UUID v4-ish shape check so we don't 422 the server with obvious typos.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Read the error code off an `ApiError` (production) OR any error carrying a
 * `.code` string (the test fixture throws plain Errors with `.status`/`.code`).
 */
function errorCode(err: unknown): string {
  if (err !== null && typeof err === 'object' && 'code' in err) {
    const code = (err as { code?: unknown }).code;
    if (typeof code === 'string') return code;
  }
  return 'UNKNOWN_ERROR';
}

export function TrainerPackageConsumptionsPage() {
  const { t } = useI18n();
  const toast = useToast();
  const auth = useAuth();

  const canReverse = auth.adminUser?.permissions.includes('trainer_package_consumption:reverse');

  const [consumptionId, setConsumptionId] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);

  const trimmedId = consumptionId.trim();
  const idLooksValid = useMemo(() => UUID_RE.test(trimmedId), [trimmedId]);

  const handleReverse = async (input: ReverseTrainerConsumptionInput) => {
    try {
      await adminTrainerConsumptionsApi.reverse(trimmedId, input);
      toast.push({
        tone: 'success',
        title: t('admin:trainer-dashboard.reverse.toast_success', {
          defaultValue: 'Consumption reversed. The session has been credited back.',
        }),
      });
      setConsumptionId('');
    } catch (err) {
      const code = errorCode(err);
      let message: string;
      if (code === 'NOT_FOUND') {
        message = t('admin:trainer-dashboard.reverse.error_not_found', {
          defaultValue: 'No consumption found with that id.',
        });
      } else if (code === 'CONSUMPTION_ALREADY_REVERSED') {
        message = t('admin:trainer-dashboard.reverse.error_already_reversed', {
          defaultValue: 'This consumption has already been reversed.',
        });
      } else {
        message = t('admin:trainer-dashboard.reverse.error_generic', {
          defaultValue: 'Could not reverse the consumption. Please try again.',
        });
      }
      toast.push({ tone: 'danger', title: message });
      // Re-throw so the dialog keeps itself open for retry/correction.
      throw err;
    }
  };

  return (
    <div className="flex flex-col gap-6" data-testid="trainer-consumptions-page">
      <PageHeader
        eyebrow={t('admin:trainer-dashboard.reverse.eyebrow', { defaultValue: 'Trainers' })}
        title={t('admin:trainer-dashboard.reverse.page_title', {
          defaultValue: 'Package consumption reversal',
        })}
        subtitle={t('admin:trainer-dashboard.reverse.page_subtitle', {
          defaultValue:
            'Credit a wrongly-recorded completed or no-show session back to a client’s package balance.',
        })}
      />

      <Banner tone="info">
        {t('admin:trainer-dashboard.reverse.page_help', {
          defaultValue:
            'Look up the consumption by its id (from the trainer event logs or the client’s report), then reverse the decrement with a reason. The ledger row is never deleted — it is marked reversed and the session is credited back.',
        })}
      </Banner>

      <Card variant="default" className="max-w-xl">
        <div className="flex flex-col gap-4">
          <FormField
            label={t('admin:trainer-dashboard.reverse.id_label', {
              defaultValue: 'Consumption id',
            })}
            helper={t('admin:trainer-dashboard.reverse.id_helper', {
              defaultValue: 'The UUID of the trainer_package_consumptions row to reverse.',
            })}
            required
          >
            <TextInput
              value={consumptionId}
              onChange={(e) => setConsumptionId(e.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
              data-testid="trainer-consumption-id-input"
              aria-label={t('admin:trainer-dashboard.reverse.id_label', {
                defaultValue: 'Consumption id',
              })}
            />
          </FormField>

          {canReverse ? (
            <div>
              <Button
                variant="primary"
                onClick={() => setDialogOpen(true)}
                disabled={!idLooksValid}
                data-testid="trainer-consumption-reverse-open"
              >
                {t('admin:trainer-dashboard.reverse.open_action', {
                  defaultValue: 'Reverse this consumption',
                })}
              </Button>
            </div>
          ) : (
            <div data-testid="trainer-consumption-no-permission">
              <Banner tone="warning">
                {t('admin:trainer-dashboard.reverse.no_permission', {
                  defaultValue: 'You do not have permission to reverse package consumptions.',
                })}
              </Banner>
            </div>
          )}
        </div>
      </Card>

      <ReverseConsumptionDialog
        open={dialogOpen && canReverse === true}
        consumptionId={trimmedId}
        onConfirm={handleReverse}
        onClose={() => setDialogOpen(false)}
      />
    </div>
  );
}

export default TrainerPackageConsumptionsPage;
